import { useEffect, useState } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import { childEntityTypes } from '@stoaria-control/schema'
import type { EffectiveConfig } from '@stoaria-control/schema'
import {
  ApiError,
  createOrgNode,
  deleteOrgNode,
  fetchEffectiveConfig,
  fetchHierarchy,
  patchNode,
  verifyNode,
} from '../lib/hierarchy-api.ts'
import type { NodePatchBody, PatchKind } from '../lib/hierarchy-api.ts'
import { useProjectJump } from '../lib/use-project-jump.ts'
import { moveTargets } from '../lib/move-targets.ts'
import { listRegistry } from '../lib/registry-api.ts'
import type { RegistryRow } from '../lib/registry-api.ts'
import { EntityDetailPanel } from '../components/EntityDetailPanel.tsx'
import type { DetailSelection } from '../components/EntityDetailPanel.tsx'
import { EntityCreateForm } from '../components/EntityCreateForm.tsx'
import { EntityTree } from '../components/EntityTree.tsx'
import type { AddTarget } from '../components/EntityTree.tsx'
import { ProjectJumpLinks } from '../components/ProjectJumpLinks.tsx'
import type { RegistryOption } from '../components/ConfigForm.tsx'
import { findInTree, toSelection } from '../lib/entity-selection.ts'
import {
  currentParentTarget,
  moveErrorText,
} from '../lib/project-move-feedback.ts'
import { Callout, PageHeader } from '../components/ui/index.ts'

interface ProjectsSearch {
  sel?: string
  selKind?: PatchKind
}

const SEL_KINDS: PatchKind[] = ['entity', 'server', 'repository']

export const Route = createFileRoute('/projects')({
  validateSearch: (search: Record<string, unknown>): ProjectsSearch => ({
    sel: typeof search.sel === 'string' ? search.sel : undefined,
    selKind: SEL_KINDS.find((kind) => kind === search.selKind),
  }),
  loader: async () =>
    withSessionRedirect(async () => {
      return await fetchHierarchy()
    }),
  component: ProjectsPage,
})

/** A pending create. A top-level entity has no parent; everything else is
 * added through the parent's derived add buttons. */
type Adding = AddTarget | { kind: 'entity'; parentId: null; entityType: '' }

function ProjectsPage() {
  const tree = Route.useLoaderData()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const [selection, setSelection] = useState<DetailSelection | null>(null)
  const [adding, setAdding] = useState<Adding | null>(null)
  const [effectiveConfig, setEffectiveConfig] =
    useState<EffectiveConfig | null>(null)
  const [nameInput, setNameInput] = useState('')
  const [hostNameInput, setHostNameInput] = useState('')
  const [agentRoles, setAgentRoles] = useState<RegistryOption[]>([])
  const [failoverConfigs, setFailoverConfigs] = useState<RegistryOption[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Registry options for the config form's role/failover pickers. Failing to
  // load them must not break the page — the pickers just render empty.
  useEffect(() => {
    // Roles carry `title`, failover configs carry `name`; fall back to the
    // id so a row is never unlabelled in the picker.
    const toOptions = (rows: RegistryRow[]): RegistryOption[] =>
      rows.map((row) => ({
        id: row.id,
        label: row.title ?? row.name ?? row.id,
      }))
    // These can resolve after the operator has navigated away; setting state
    // then is a no-op React warns about, so drop late results.
    let cancelled = false
    void listRegistry('agent-roles')
      .then((rows) => {
        if (!cancelled) setAgentRoles(toOptions(rows))
      })
      .catch(() => {
        if (!cancelled) setAgentRoles([])
      })
    void listRegistry('failover-configs')
      .then((rows) => {
        if (!cancelled) setFailoverConfigs(toOptions(rows))
      })
      .catch(() => {
        if (!cancelled) setFailoverConfigs([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Restore selection from the URL on first render / refresh.
  useEffect(() => {
    if (!search.sel || !search.selKind || selection) return
    const found = findInTree(tree, search.selKind, search.sel)
    if (found) select(search.selKind, found)
  }, [search.sel, search.selKind, tree])

  function startAdd(target: Adding) {
    setAdding(target)
    setSelection(null)
    setNameInput('')
    setHostNameInput('')
    setEffectiveConfig(null)
    setError(null)
  }

  async function handleCreate() {
    if (!adding) return
    setSaving(true)
    setError(null)
    try {
      await createOrgNode({
        name: nameInput.trim(),
        parentId: adding.parentId,
        kind: adding.kind,
        entityType: adding.kind === 'entity' ? adding.entityType : '',
        hostName: adding.kind === 'server' ? hostNameInput.trim() : '',
      })
      setAdding(null)
      await router.invalidate()
    } catch (err) {
      // The API's 409 is a code, not guidance.
      if (err instanceof ApiError && err.status === 409)
        setError('That entity already has a server — only one is allowed.')
      else setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setSaving(false)
    }
  }

  function select(
    kind: PatchKind,
    found: NonNullable<ReturnType<typeof findInTree>>,
  ) {
    setAdding(null)
    const base = toSelection(found)
    const targets = moveTargets(tree.roots, { kind, id: base.id })
    const current = currentParentTarget(found)
    const next = {
      ...base,
      moveTargets:
        current && !targets.some((t) => t.id === current.id)
          ? [current, ...targets]
          : targets,
    }
    setSelection(next)
    setEffectiveConfig(null)
    setError(null)
    void navigate({ search: { sel: next.id, selKind: kind }, replace: true })
    // Every entity is a project, so every entity has an effective config.
    if (kind === 'entity') {
      fetchEffectiveConfig(next.id)
        .then(setEffectiveConfig)
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : 'load failed')
        })
    }
  }

  function selectById(kind: PatchKind, id: string) {
    const found = findInTree(tree, kind, id)
    if (found) select(kind, found)
  }

  async function handleVerify(nodeId: string) {
    await verifyNode(nodeId)
  }

  async function handleDelete(_kind: PatchKind, id: string, name: string) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return
    setSaving(true)
    setError(null)
    try {
      await deleteOrgNode(id)
      // The detail panel edits a row that no longer exists — clear it (and
      // the ?sel= deep link pointing at it) rather than leave a ghost form.
      if (selection?.id === id) {
        setSelection(null)
        void navigate({ search: {}, replace: true })
      }
      await router.invalidate()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409)
        setError(
          `"${name}" still has entities, servers or repositories beneath it — delete or move those first.`,
        )
      else setError(err instanceof Error ? err.message : 'delete failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleSave(body: NodePatchBody) {
    if (!selection) return
    setSaving(true)
    setError(null)
    try {
      await patchNode(selection.kind, selection.id, body)
      await router.invalidate()
    } catch (err) {
      const moveError = moveErrorText(err)
      if (moveError) setError(moveError)
      else setError(err instanceof Error ? err.message : 'save failed')
    } finally {
      setSaving(false)
    }
  }

  const jump = useProjectJump(tree, selection)

  const addingParent =
    adding && adding.parentId !== null
      ? findInTree(tree, 'entity', adding.parentId)
      : null
  const addingTypeOptions = addingParent
    ? childEntityTypes({
        details: addingParent.node.entityDetails,
        depth: addingParent.node.depth,
        scopeLevels: addingParent.ancestors[0]?.entityDetails.scopeLevels ?? [],
      })
    : []

  return (
    // `p-8`: the shell's `<main>` is deliberately unpadded and every page
    // owns its own gutter. The comment that used to sit here claimed the
    // shell supplied it, which was never true — this page and /sessions
    // were the only two rendering flush against the sidebar.
    <div className="p-8">
      <PageHeader
        title="Projects"
        description="Every entity in the tree is a project: pick one to edit what it is, where it runs and what it passes down."
      />
      <div className="flex gap-8">
        <div>
          <EntityTree
            roots={tree.roots}
            onSelect={selectById}
            onAdd={startAdd}
            onDelete={(kind, id, name) => void handleDelete(kind, id, name)}
            onAddRoot={() => {
              startAdd({ kind: 'entity', parentId: null, entityType: '' })
            }}
            selectedId={selection?.id ?? null}
          />
          {/* Delete failures surface next to the tree when no panel is open to
            carry them (the deleted selection is cleared on success). */}
          {error && !adding && !selection && (
            <div className="mt-2">
              <Callout tone="danger">{error}</Callout>
            </div>
          )}
        </div>
        {adding && (
          <EntityCreateForm
            kind={adding.kind}
            entityType={adding.kind === 'entity' ? adding.entityType : ''}
            typeOptions={addingTypeOptions}
            name={nameInput}
            onNameChange={setNameInput}
            hostName={hostNameInput}
            onHostNameChange={setHostNameInput}
            saving={saving}
            error={error}
            onCreate={() => void handleCreate()}
          />
        )}
        {selection && (
          // Keyed so switching selection remounts the panel and reseeds every
          // field from the new record.
          <EntityDetailPanel
            key={selection.id}
            selection={selection}
            effectiveConfig={effectiveConfig}
            agentRoles={agentRoles}
            failoverConfigs={failoverConfigs}
            saving={saving}
            error={error}
            onSave={(body) => void handleSave(body)}
            onVerify={handleVerify}
            crossLinks={
              jump && <ProjectJumpLinks nodeId={selection.id} {...jump} />
            }
          />
        )}
      </div>
    </div>
  )
}
